import pathJoin from './path-join'
import qs from './qs'
import Events from './events'

export { pathJoin, qs, Events }

var proto = Object.prototype
var gpo = Object.getPrototypeOf

// 返回对象类型
export function type(obj) {
    return (obj === null || obj === undefined) ? String(obj) : Object.prototype.toString.call(obj).match(/\[object (\w+)\]/)[1].toLowerCase()
}

// 是否纯对象
export function isPojo(obj) {
    if (obj === null || typeof obj !== "object") {
        return false;
    }
    return gpo(obj) === proto;
}

// 是否数字
export function isNumber(num) {
    if (typeof num === 'number') {
        return num - num === 0
    }
    if (typeof num === 'string' && num.trim() !== '') {
        return Number.isFinite ? Number.isFinite(+num) : isFinite(+num)
    }
    return false
}

// 简易函数柯里化
export const curry = (
    f, arr = []
) => (...args) => (
    a => a.length === f.length ?
    f(...a) :
    curry(f, a)
)([...arr, ...args])

// 单例
export function once(fn) {
    var called = false

    return function(...args) {
        if (called === false) {
            called = true
            return fn.call(this, ...args)
        }
    }
}

// 取并集
export function setUnion(s1, s2) {
    return Array.from(new Set([...s1, ...s2]))
}

// obj比较
export function objMatch(source, target) {
    if (type(target) === "object") {
        if (type(source) !== "object") {
            return false
        }

        return Object.keys(target).every(key => objMatch(source[key], target[key]))
    }

    if (type(target) === "array") {
        if (type(source) !== "array") {
            return false
        }

        return target.every((t, i) => objMatch(source[i], t))
    }

    return source === target
}

// object递归合并（array浅复制覆盖）
export function objectMerge(to, from) {
    Object.keys(from).forEach(k => {
        var toVal = to[k],
            fromVal = from[k],
            isToPojo = isPojo(toVal),
            isFromPojo = isPojo(fromVal)

        if (toVal !== fromVal) {
            if (isToPojo && isFromPojo) {
                objectMerge(toVal, fromVal)
            } else if (typeof toVal == "undefined" && isFromPojo) {
                toVal = to[k] = {}
                objectMerge(toVal, fromVal)
            } else {
                to[k] = typeof fromVal == "undefined" ? to[k] : type(fromVal) == "array" ? [...fromVal] : fromVal
            }
        }
    })

    return to
}

// deepCopy
function _deepCopy(data, options, __deepCopy__) {
    const isArray = type(data) == "array"
    /**
     * 只处理纯对象。自定义对象边际场景太多，如：
     * 1. IE11下 type(BigNumber) == “object"，chrome下 type(BigNumber) == BifNumber"
     **/
    const isPureObject = isPojo(data)

    let o,
        exclude = options.exclude || [], // 排除属性
        depth = options.depth >= 0 ? options.depth : -1; // 控制递归层级

    if (isArray) {
        o = [];
    } else if (isPureObject) {
        o = {};
    } else {
        return data;
    }

    // 层级+1
    __deepCopy__.depth++
    // 超出最大复制层级
    if (depth >= 0 && __deepCopy__.depth > depth) {
        return o;
    }

    if (isArray) {
        for (let i = 0; i < data.length; i++) {
            o.push(_deepCopy(data[i], options, __deepCopy__))
        }
    } else if (isPureObject) {
        for (let i in data) {
            // 字段排除
            if (exclude.indexOf(i) < 0) {
                o[i] = _deepCopy(data[i], options, __deepCopy__)
            }
        }
    }
    return o;
}

function deepCopy(data, options = {}) {
    // 递归数据
    var __deepCopy__ = {
        depth: -1
    }

    return _deepCopy(data, options, __deepCopy__)
}

export { deepCopy }

// 金额格式化，默认2位小数，逗号分隔
export function currency(s, n) {
    // 空字符直接转为0
    if (s == "" || s == null || s == undefined) {
        s = "0"
    }

    n = n >= 0 && n <= 20 ? n : 2;
    s = parseFloat((s + "").replace(/[^\d\.-]/g, "")).toFixed(n) + "";

    var l = s.split(".")[0].split("").reverse(),
        r = s.split(".")[1] || '',
        t = "";

    for (var i = 0; i < l.length; i++) {
        t += l[i] + ((i + 1) % 3 == 0 && (i + 1) != l.length && l[i + 1] != "-" ? "," : "");
    }

    return t.split("").reverse().join("") + (r.length > 0 ? "." + r : '');
}

// 单词首字母大写
export function capitalize(s) {
    if (!s) return ''
    s = s.toString()

    return s.charAt(0).toUpperCase() + s.slice(1)
}

// rgb转16进制 https://github.com/christian-bromann/rgb2hex/blob/master/index.js
export function rgb2hex(color) {
    if (typeof color !== 'string') {
        // throw error of input isn't typeof string
        throw new Error('color has to be type of `string`')
    } else if (color.substr(0, 1) === '#') {
        // or return if already rgb color
        return {
            hex: color,
            alpha: 1
        }
    }

    /**
     * strip spaces
     */
    var strippedColor = color.replace(/\s+/g, '')

    /**
     * parse input
     */
    var digits = /(.*?)rgb(a)??\((\d{1,3}),(\d{1,3}),(\d{1,3})(,([01]|0??\.([0-9]{0,})))??\)/.exec(strippedColor)

    if (!digits) {
        // or throw error if input isn't a valid rgb(a) color
        throw new Error('given color (' + color + ') isn\'t a valid rgb or rgba color')
    }

    var red = parseInt(digits[3], 10)
    var green = parseInt(digits[4], 10)
    var blue = parseInt(digits[5], 10)
    var alpha = digits[6] ? /([0-9\.]+)/.exec(digits[6])[0] : '1'
    var rgb = ((blue | green << 8 | red << 16) | 1 << 24).toString(16).slice(1)

    // parse alpha value into float
    if (alpha.substr(0, 1) === '.') {
        alpha = parseFloat('0' + alpha)
    }

    // cut alpha value after 2 digits after comma
    alpha = parseFloat(Math.round(alpha * 100)) / 100

    return {
        hex: '#' + rgb.toString(16),
        alpha: alpha
    }
}

// 异步队列
export function runQueue(queue, fn, cb) {
    const stopQueue = index => {
        cb && cb(index >= queue.length)
    }

    const step = index => {
        if (index >= queue.length) {
            cb && cb(index >= queue.length)
        } else {
            if (queue[index]) {
                fn(queue[index], stop => {
                    stop === false ? stopQueue(index) : step(index + 1)
                })
            } else {
                step(index + 1)
            }
        }
    }

    step(0)
}

// 节流
export function throttle(delay, callback, debounceMode) {
    var timeoutID
    var lastExec = 0

    function wrapper() {
        var self = this;
        var elapsed = Number(new Date()) - lastExec
        var args = arguments

        // Execute `callback` and update the `lastExec` timestamp.
        function exec() {
            lastExec = Number(new Date())
            callback.apply(self, args)
        }

        function clear() {
            if (timeoutID) {
                clearTimeout(timeoutID)
                timeoutID = undefined
            }
        }

        // Clear any existing timeout.
        if (timeoutID) {
            clearTimeout(timeoutID)
        }

        // debounce mode
        if (debounceMode) {
            clear()

            timeoutID = setTimeout(exec, delay)
        }
        // throttle mode
        else if (elapsed > delay) {
            exec()
        }
    }

    // Return the wrapper function.
    return wrapper
}

// 防抖
export function debounce(delay, callback){
    return throttle(delay, callback, true)
}

// 是否高序位
export function isHighSurrogate(codePoint) {
    // 1101100000000000 - 1101101111111111
    return codePoint >= 0xd800 && codePoint <= 0xdbff;
}

// 利用CJK中日韩统一表意文字计算字符串长度
export function cjkLength(str) {
    if (typeof str !== "string") {
        throw new Error("cnLength只接受字符串参数")
    }

    var len = 0

    for (var i = 0; i < str.length; i++) {
        var code = str[i].charCodeAt(0)

        // smp
        if (isHighSurrogate(code)) {
            len += 1
            i++
        }
        // cjk range@https://blog.csdn.net/chivalrousli/article/details/77412329
        else if (code >= 0x2E80 && code <= 0xFE4F) {
            len += 1
        }
        // 西文
        else {
            len += 0.54
        }
    }

    return len
}

// cjk计算宽度，高效，极少数符号会漏掉，宽度也存在一些小偏差（西字占中文宽度一半并不总是适用）
export function getTextWidth(str, fontSize = 14) {
    return cjkLength(str) * fontSize
}

// id、pid结构转层级签套(id要唯一)
export function listToTree(list, {
    id: _id = "id",
    pId: _pId = "pId",
    rootId: _rootId = "",
    level: _level = "level",
    isEnd: _isEnd = "isEnd",
    children: _children = "children",
    copy = true,
    done = function(tree, map, list) {}
} = {}) {
    var map = {},
        tree

    // copy
    if (copy) {
        list = list.map(t => Object.assign({}, t))
    }

    // 建立索引
    list.forEach(t => {
        map[t[_id]] = t
        t[_isEnd] = true
        t[_children] = []
    })

    // 建立父子关系
    list.forEach(t => {
        var parent = map[t[_pId]]

        t.isTop = !parent

        parent && (parent[_children] ? parent[_children].push(t) : parent[_children] = [t], parent[_isEnd] = false)
    })

    // 建立level
    function setLevel(node, level) {
        node[_level] = level

        if (node[_children]) {
            node[_children].forEach(t => setLevel(t, level + 1))
        }
    }

    list.filter(t => t.isTop).forEach(t => setLevel(t, 1))

    tree = list.filter(t => {
        if (_rootId) {
            return t[_pId] == _rootId
        } else {
            return t.isTop
        }
    })

    // 转换完成后回调
    done(tree, map, list)

    // 返回tree
    return tree
}

// 深度遍历
export function deepTraverse(forest, callback, options = {}) {
    if (type(forest) != "array") {
        forest = [forest]
    }

    var { children = "children" } = options
    var isContinue = true // 控制遍历是否继续执行 

    function iterate(node, parent, options) {
        isContinue = callback(node, parent, options)

        // 默认继续
        if (typeof isContinue == "undefined") {
            isContinue = true
        }

        if (isContinue && node[children]) {
            node[children].every(n => iterate(n, node, options))
        }

        return isContinue
    }

    forest.forEach(root => {
        if (iterate(root, null, options) == false) {
            return
        }
    })
}

// 广度优先遍历
export function breadthTraverse(forest, callback, options = {}) {
    if (type(forest) != "array") {
        forest = [forest]
    }

    var { children = "children" } = options
    var stack = [...forest],
        node,
        prevNode = null,
        isContinue = true

    while ((node = stack.shift())) {
        isContinue = callback(node, prevNode, options)
        prevNode = node

        // 默认继续
        if (typeof isContinue == "undefined") {
            isContinue = true
        }

        if (isContinue && node[children]) {
            stack.push(...node.children)
        }
    }
}

// string hashcode
export function hashCode(s) {
    var h = 0,
        l = s.length,
        i = 0

    if (l > 0)
        while (i < l)
            h = (h << 5) - h + s.charCodeAt(i++) | 0
    return h
}